// ═══════════════════════════════════════════════════════════════
// FamilyGraph.js — Graphe famille React Flow (Family P2c)
// ═══════════════════════════════════════════════════════════════
// Container principal. Charge /api/projects/:id/family/graph avec
// include CSV complet, bâtit les nodes/edges, applique dagre pour
// le layout, expose les actions (Nouvelle personne / Nouvelle
// cohabitation / Paramètres).
// ═══════════════════════════════════════════════════════════════

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ReactFlow,
    ReactFlowProvider,
    Background,
    Controls,
    MiniMap,
    MarkerType,
    useNodesState,
    useEdgesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import {
    Users, UserPlus, Home, Settings, RefreshCw, Loader2, Link2, X,
} from 'lucide-react';

import PersonNode from './PersonNode';
import CoupleNode from './CoupleNode';
import RelationEdge from './RelationEdge';
import PersonDetailPanel from './PersonDetailPanel';
import PersonEditModal from './PersonEditModal';
import CohabitationEditModal from './CohabitationEditModal';
import CanvasSettingsModal from './CanvasSettingsModal';

import { getFamilyGraph, listRelationTypes, createRelationship, deleteRelationship, removeCohabitationMember } from '../../services/familyApi';
import { useTheme } from '../../contexts/ThemeContext';
import { toast, confirm } from '../ui/Toast';

// Palette cohabitations (accessibility-safe, cycle sur 8)
const COHAB_PALETTE = [
    '#1D4ED8', '#a855f7', '#10b981', '#f59e0b',
    '#ef4444', '#06b6d4', '#ec4899', '#84cc16',
];

const NODE_TYPES = { person: PersonNode, couple: CoupleNode };
const EDGE_TYPES = { relation: RelationEdge };

// Types parentaux (sens parent→enfant) — affichés en edge visible
const PARENT_TYPES = new Set(['pere', 'mere', 'grand_pere', 'grand_mere', 'tuteur_legal', 'parrain', 'marraine', 'oncle', 'tante', 'beau_pere', 'belle_mere']);
// Types masqués (réciproques implicites d'une relation parentale)
const CHILD_TYPES = new Set(['enfant', 'pupille', 'filleul']);
// Types sibling / même génération — doivent être rendus horizontalement
const SIBLING_TYPES = new Set(['frere', 'soeur', 'cousin', 'cousine', 'demi_frere', 'demi_soeur']);

// ─── Dagre layout ──────────────────────────────────────────────
const NODE_W = 158;
const NODE_H = 68;
const COUPLE_W = 30;
const COUPLE_H = 30;

// `isLayoutEdge` : filtre qui reçoit un edge et retourne true si dagre doit le considérer
// pour le calcul hiérarchique. Les edges siblings (frère/sœur) doivent être exclus sinon
// dagre les met sur des rangs différents.
const layoutWithDagre = (nodes, edges, direction = 'TB', isLayoutEdge = () => true) => {
    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({ rankdir: direction, nodesep: 40, ranksep: 58, marginx: 20, marginy: 20 });

    nodes.forEach((n) => {
        const w = n.type === 'couple' ? COUPLE_W : NODE_W;
        const h = n.type === 'couple' ? COUPLE_H : NODE_H;
        g.setNode(n.id, { width: w, height: h });
    });
    edges.filter(isLayoutEdge).forEach((e) => g.setEdge(e.source, e.target));
    dagre.layout(g);

    return nodes.map((n) => {
        const pos = g.node(n.id);
        const w = n.type === 'couple' ? COUPLE_W : NODE_W;
        const h = n.type === 'couple' ? COUPLE_H : NODE_H;
        return {
            ...n,
            position: { x: pos.x - w / 2, y: pos.y - h / 2 },
            sourcePosition: 'bottom',
            targetPosition: 'top',
        };
    });
};

// ─── FamilyGraph internal ──────────────────────────────────────
const FamilyGraphInner = ({ projectId }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [graph, setGraph] = useState(null);
    const [relationTypes, setRelationTypes] = useState([]);

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    const [selectedPersonId, setSelectedPersonId] = useState(null);
    const [editingPerson, setEditingPerson] = useState(null); // {open, person|null}
    const [editingCohab, setEditingCohab] = useState(null);
    const [showSettings, setShowSettings] = useState(false);
    const [addRelationSource, setAddRelationSource] = useState(null); // person object

    const containerRef = useRef(null);

    // ─── Chargement ────────────────────────────────────────────
    const loadGraph = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        else setRefreshing(true);
        try {
            const [g, types] = await Promise.all([
                getFamilyGraph(projectId, {
                    include: 'persons,relationships,cohabitations,canvas,doc_categories,actions_pending',
                }),
                listRelationTypes(projectId).catch(() => []),
            ]);
            setGraph(g);
            setRelationTypes(types?.types || []);
        } catch (e) {
            toast.error(`Chargement graphe famille : ${e.message}`);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [projectId]);

    useEffect(() => { loadGraph(); }, [loadGraph]);

    // ─── Nodes & edges depuis graph ─────────────────────────────
    useEffect(() => {
        if (!graph) return;
        const persons = graph.persons || [];
        const relationships = graph.relationships || [];
        const cohabitations = graph.cohabitations || [];

        // Map person_id → couleur cohabitation primaire
        const primaryCohab = cohabitations.find((c) => c.is_primary);
        const cohabIndex = new Map();
        cohabitations.forEach((co, idx) => cohabIndex.set(co.id, COHAB_PALETTE[idx % COHAB_PALETTE.length]));

        const personCohabColor = new Map();
        persons.forEach((p) => {
            if (primaryCohab && (primaryCohab.members || []).some((m) => m.person_id === p.id)) {
                personCohabColor.set(p.id, cohabIndex.get(primaryCohab.id));
                return;
            }
            for (const co of cohabitations) {
                if ((co.members || []).some((m) => m.person_id === p.id)) {
                    personCohabColor.set(p.id, cohabIndex.get(co.id));
                    return;
                }
            }
        });

        // ─── Détection des couples & enfants communs ──────────
        // 1. Couples = relations type=conjoint (symétriques). Clé canonique: min-max.
        const coupleKeyOf = (a, b) => `${Math.min(a, b)}-${Math.max(a, b)}`;
        const couples = new Map(); // key → { a, b }
        relationships.forEach((r) => {
            if (r.type === 'conjoint') {
                const key = coupleKeyOf(r.person_a_id, r.person_b_id);
                if (!couples.has(key)) couples.set(key, { a: r.person_a_id, b: r.person_b_id });
            }
        });

        // 2. Pour chaque couple, collecter les enfants communs.
        // Enfant commun = personne X telle qu'il existe rel(parent→X, type parental) depuis A ET depuis B.
        const parentsOfByChild = new Map(); // childId → Set<parentId>
        relationships.forEach((r) => {
            if (PARENT_TYPES.has(r.type)) {
                if (!parentsOfByChild.has(r.person_b_id)) parentsOfByChild.set(r.person_b_id, new Set());
                parentsOfByChild.get(r.person_b_id).add(r.person_a_id);
            }
        });

        const coupleByKey = new Map(); // key → { key, a, b, childIds: Set }
        const sharedByParent = new Map(); // parentId → Set<couple key> pour savoir quelles relations cacher
        couples.forEach((cp, key) => {
            const shared = new Set();
            parentsOfByChild.forEach((parentSet, childId) => {
                if (parentSet.has(cp.a) && parentSet.has(cp.b)) shared.add(childId);
            });
            coupleByKey.set(key, { key, a: cp.a, b: cp.b, childIds: shared });
            if (shared.size > 0) {
                if (!sharedByParent.has(cp.a)) sharedByParent.set(cp.a, new Set());
                if (!sharedByParent.has(cp.b)) sharedByParent.set(cp.b, new Set());
                sharedByParent.get(cp.a).add(key);
                sharedByParent.get(cp.b).add(key);
            }
        });

        // Ne garder que les couples qui ont au moins un enfant commun (utile visuellement)
        const activeCouples = [...coupleByKey.values()].filter((c) => c.childIds.size > 0);

        // ─── Build nodes ───────────────────────────────────────
        const rawNodes = persons.map((p) => ({
            id: String(p.id),
            type: 'person',
            position: { x: 0, y: 0 },
            data: {
                person: p,
                cohabitationColor: personCohabColor.get(p.id),
                isDark,
            },
        }));

        // Ajoute les nœuds couples virtuels
        activeCouples.forEach((cp) => {
            const color = personCohabColor.get(cp.a) || personCohabColor.get(cp.b) || '#1D4ED8';
            rawNodes.push({
                id: `couple-${cp.key}`,
                type: 'couple',
                position: { x: 0, y: 0 },
                data: { isDark, color },
                selectable: false,
            });
        });

        // ─── Build edges ───────────────────────────────────────
        const rawEdges = [];
        const hiddenCoupleChildRels = new Set(); // ids de relation à cacher (cachées par le couple)

        // Marque comme cachée toute rel parentale dont le parent est dans un couple et l'enfant commun
        relationships.forEach((r) => {
            if (!PARENT_TYPES.has(r.type)) return;
            const keys = sharedByParent.get(r.person_a_id);
            if (!keys) return;
            for (const key of keys) {
                const cp = coupleByKey.get(key);
                if (cp && cp.childIds.has(r.person_b_id)) {
                    hiddenCoupleChildRels.add(r.id);
                }
            }
        });

        // Cache aussi les relations enfant→parent (réciproques) pour ces mêmes couples
        relationships.forEach((r) => {
            if (!CHILD_TYPES.has(r.type)) return;
            // enfant=person_a, parent=person_b
            const keys = sharedByParent.get(r.person_b_id);
            if (!keys) return;
            for (const key of keys) {
                const cp = coupleByKey.get(key);
                if (cp && cp.childIds.has(r.person_a_id)) {
                    hiddenCoupleChildRels.add(r.id);
                }
            }
        });

        // Cache toutes les relations CHILD_TYPES par défaut (réciproques des parentales — redondantes visuellement)
        relationships.forEach((r) => {
            if (CHILD_TYPES.has(r.type)) hiddenCoupleChildRels.add(r.id);
        });

        // Cache l'edge conjoint direct quand un couple-node existe pour ce couple
        // (sinon dagre voit 2 chemins A↔B et A↔couple↔B, et décale Sophie plus bas).
        relationships.forEach((r) => {
            if (r.type === 'conjoint') {
                const key = coupleKeyOf(r.person_a_id, r.person_b_id);
                if (coupleByKey.has(key) && coupleByKey.get(key).childIds.size > 0) {
                    hiddenCoupleChildRels.add(r.id);
                }
            }
        });

        // Fusion des paires sibling (frere/soeur/cousin…) : 1 seul edge par paire A-B
        // Label combiné selon les types trouvés (ex: "Frère / Sœur", "Cousins").
        const siblingPairs = new Map(); // key = min-max → { labels: Set, anyId, a, b }
        const fusedSiblingRelIds = new Set();
        relationships.forEach((r) => {
            if (hiddenCoupleChildRels.has(r.id)) return;
            if (!SIBLING_TYPES.has(r.type)) return;
            const key = `${Math.min(r.person_a_id, r.person_b_id)}-${Math.max(r.person_a_id, r.person_b_id)}`;
            if (!siblingPairs.has(key)) {
                siblingPairs.set(key, {
                    a: Math.min(r.person_a_id, r.person_b_id),
                    b: Math.max(r.person_a_id, r.person_b_id),
                    labels: new Set(),
                    anyId: r.id,
                });
            }
            siblingPairs.get(key).labels.add(r.type_label || r.type);
            fusedSiblingRelIds.add(r.id);
        });

        // Relations normales non-sibling (non cachées)
        relationships.forEach((r) => {
            if (hiddenCoupleChildRels.has(r.id)) return;
            if (fusedSiblingRelIds.has(r.id)) return;
            rawEdges.push({
                id: `rel-${r.id}`,
                source: String(r.person_a_id),
                target: String(r.person_b_id),
                type: 'relation',
                data: {
                    label: r.type_label || r.type,
                    source: r.source,
                    relationId: r.id,
                    isDark,
                    isSibling: false,
                },
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    color: isDark ? '#6b7280' : '#94a3b8',
                    width: 14,
                    height: 14,
                },
            });
        });

        // Un edge par paire sibling — sans flèche, label combiné
        siblingPairs.forEach((pair, key) => {
            const labels = [...pair.labels];
            const combined = labels.length > 1 ? labels.join(' / ') : labels[0];
            rawEdges.push({
                id: `sibling-${key}`,
                source: String(pair.a),
                target: String(pair.b),
                type: 'relation',
                data: {
                    label: combined,
                    source: 'manual',
                    relationId: pair.anyId,
                    isDark,
                    isSibling: true,
                },
            });
        });

        // Edges A↔couple + couple→enfants
        activeCouples.forEach((cp) => {
            const coupleId = `couple-${cp.key}`;
            // A → couple (trait discret, pas de flèche, pas de label)
            rawEdges.push({
                id: `${coupleId}-a`,
                source: String(cp.a),
                target: coupleId,
                type: 'relation',
                data: { label: '', source: 'manual', isDark, muted: true },
            });
            rawEdges.push({
                id: `${coupleId}-b`,
                source: String(cp.b),
                target: coupleId,
                type: 'relation',
                data: { label: '', source: 'manual', isDark, muted: true },
            });
            // couple → enfants
            cp.childIds.forEach((childId) => {
                rawEdges.push({
                    id: `${coupleId}-child-${childId}`,
                    source: coupleId,
                    target: String(childId),
                    type: 'relation',
                    data: { label: 'Enfant', source: 'manual', isDark },
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        color: isDark ? '#6b7280' : '#94a3b8',
                        width: 14,
                        height: 14,
                    },
                });
            });
        });

        // Layout : dagre ignore les edges siblings (frère/sœur) pour les mettre au même rang
        const isLayoutEdge = (e) => {
            // Les edges siblings ont data.isSibling = true (voir plus bas)
            return !e.data?.isSibling;
        };
        const laidOut = layoutWithDagre(rawNodes, rawEdges, 'TB', isLayoutEdge);

        // Routage des handles selon géométrie + type de relation
        const nodeById = Object.fromEntries(laidOut.map((n) => [n.id, n]));
        const routedEdges = rawEdges.map((e) => {
            const s = nodeById[e.source];
            const t = nodeById[e.target];
            if (!s || !t) return e;
            const sW = s.type === 'couple' ? COUPLE_W : NODE_W;
            const sH = s.type === 'couple' ? COUPLE_H : NODE_H;
            const tW = t.type === 'couple' ? COUPLE_W : NODE_W;
            const tH = t.type === 'couple' ? COUPLE_H : NODE_H;
            const sCenter = { x: s.position.x + sW / 2, y: s.position.y + sH / 2 };
            const tCenter = { x: t.position.x + tW / 2, y: t.position.y + tH / 2 };
            const dx = tCenter.x - sCenter.x;
            const dy = tCenter.y - sCenter.y;

            let sourceHandle, targetHandle;
            if (e.data?.isSibling) {
                // Sibling : toujours horizontal — sort par le côté du source vers le côté du target
                if (dx >= 0) { sourceHandle = 'sr'; targetHandle = 'tl'; }
                else { sourceHandle = 'sl'; targetHandle = 'tr'; }
            } else if (Math.abs(dy) >= Math.abs(dx)) {
                // Vertical dominant — top/bottom
                if (dy > 0) { sourceHandle = 's'; targetHandle = 't'; }
                else { sourceHandle = 'sl'; targetHandle = 't'; }
            } else {
                // Horizontal dominant — côté
                if (dx > 0) { sourceHandle = 'sr'; targetHandle = 'tl'; }
                else { sourceHandle = 'sl'; targetHandle = 'tr'; }
            }
            return { ...e, sourceHandle, targetHandle };
        });

        setNodes(laidOut);
        setEdges(routedEdges);
    }, [graph, isDark, setNodes, setEdges]);

    // ─── Handlers noeud ────────────────────────────────────────
    const onNodeClick = useCallback((_evt, node) => {
        setSelectedPersonId(Number(node.id));
    }, []);

    const onPaneClick = useCallback(() => setSelectedPersonId(null), []);

    const onEdgeClick = useCallback(async (evt, edge) => {
        evt.stopPropagation();
        const relId = edge.data?.relationId;
        if (!relId) return;
        const ok = await confirm(
            `Supprimer cette relation « ${edge.data.label} » ?`,
            { danger: true, confirmText: 'Supprimer' }
        );
        if (!ok) return;
        try {
            await deleteRelationship(projectId, relId);
            toast.success('Relation supprimée');
            loadGraph(true);
        } catch (e) {
            toast.error(`Échec : ${e.message}`);
        }
    }, [projectId, loadGraph]);

    const selectedPerson = useMemo(() => {
        if (!selectedPersonId || !graph) return null;
        return (graph.persons || []).find((p) => p.id === selectedPersonId) || null;
    }, [selectedPersonId, graph]);

    // ─── Actions ────────────────────────────────────────────────
    const handleRemoveFromCohab = async (co, person) => {
        try {
            await removeCohabitationMember(projectId, co.id, person.id);
            toast.success('Retiré de la cohabitation');
            loadGraph(true);
        } catch (e) {
            toast.error(`Échec : ${e.message}`);
        }
    };

    // ─── Rendu ─────────────────────────────────────────────────
    const surface = isDark ? '#0f0f1e' : '#f8fafc';
    const surfaceAlt = isDark ? '#16162a' : '#ffffff';
    const textPrimary = isDark ? '#e5e7eb' : '#1e293b';
    const textSecondary = isDark ? '#94a3b8' : '#64748b';
    const border = isDark ? '#2a2a44' : '#e2e8f0';

    if (loading) {
        return (
            <div style={{
                height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: surface, color: textSecondary, flexDirection: 'column', gap: 10,
            }}>
                <Loader2 size={24} className="animate-spin" />
                <span style={{ fontSize: 13 }}>Chargement du graphe famille…</span>
            </div>
        );
    }

    const cohabs = graph?.cohabitations || [];
    const persons = graph?.persons || [];
    const relationships = graph?.relationships || [];

    return (
        <div
            ref={containerRef}
            style={{
                position: 'relative', height: '100%', width: '100%',
                background: surface, color: textPrimary, overflow: 'hidden',
                borderRadius: 12, border: `1px solid ${border}`,
            }}
        >
            {/* Header toolbar */}
            <div style={{
                position: 'absolute', top: 10, left: 10, right: 10, zIndex: 5,
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            }}>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 12px', background: surfaceAlt,
                    border: `1px solid ${border}`, borderRadius: 10,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: 12,
                }}>
                    <Users size={14} style={{ color: '#1D4ED8' }} />
                    <strong>{persons.length}</strong>
                    <span style={{ color: textSecondary }}>personnes</span>
                    <span style={{ color: textSecondary, margin: '0 4px' }}>·</span>
                    <strong>{relationships.length}</strong>
                    <span style={{ color: textSecondary }}>relations</span>
                    <span style={{ color: textSecondary, margin: '0 4px' }}>·</span>
                    <strong>{cohabs.length}</strong>
                    <span style={{ color: textSecondary }}>cohabitations</span>
                </div>

                <div style={{ flex: 1 }} />

                <ToolbarButton
                    icon={<UserPlus size={14} />}
                    label="Personne"
                    onClick={() => setEditingPerson({ open: true, person: null })}
                    primary
                    isDark={isDark}
                />
                <ToolbarButton
                    icon={<Home size={14} />}
                    label="Cohabitation"
                    onClick={() => setEditingCohab({ open: true, cohabitation: null })}
                    isDark={isDark}
                />
                <ToolbarButton
                    icon={<Settings size={14} />}
                    label="Paramètres"
                    onClick={() => setShowSettings(true)}
                    isDark={isDark}
                />
                <ToolbarButton
                    icon={refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    label=""
                    onClick={() => loadGraph(true)}
                    isDark={isDark}
                    iconOnly
                    title="Rafraîchir"
                />
            </div>

            {/* Légende cohabitations */}
            {cohabs.length > 0 && (
                <div style={{
                    position: 'absolute', bottom: 14, left: 14, zIndex: 5,
                    padding: '10px 12px', background: surfaceAlt,
                    border: `1px solid ${border}`, borderRadius: 10,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)', maxWidth: 280,
                }}>
                    <div style={{
                        fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
                        color: textSecondary, marginBottom: 8,
                    }}>
                        Cohabitations
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {cohabs.map((co, idx) => {
                            const color = COHAB_PALETTE[idx % COHAB_PALETTE.length];
                            return (
                                <button
                                    key={co.id}
                                    onClick={() => setEditingCohab({ open: true, cohabitation: co })}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 8,
                                        padding: '4px 6px', borderRadius: 6,
                                        background: 'transparent', border: 'none', cursor: 'pointer',
                                        color: textPrimary, textAlign: 'left',
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.background = isDark ? '#1e1e38' : '#f1f5f9')}
                                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                >
                                    <span style={{
                                        display: 'inline-block', width: 10, height: 10,
                                        borderRadius: 3, background: color, flexShrink: 0,
                                    }} />
                                    <span style={{ fontSize: 12, fontWeight: 500 }}>{co.label}</span>
                                    {co.is_primary && (
                                        <span style={{
                                            fontSize: 9, color: '#10b981',
                                            background: '#10b98118', padding: '1px 4px',
                                            borderRadius: 4, textTransform: 'uppercase', letterSpacing: 0.4,
                                        }}>primaire</span>
                                    )}
                                    <span style={{ fontSize: 10, color: textSecondary, marginLeft: 'auto' }}>
                                        {(co.members || []).length}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Empty state */}
            {persons.length === 0 && (
                <div style={{
                    position: 'absolute', inset: 0, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 14,
                    pointerEvents: 'none', color: textSecondary, textAlign: 'center', padding: 24,
                }}>
                    <Users size={40} strokeWidth={1.2} style={{ opacity: 0.5 }} />
                    <div style={{ maxWidth: 320 }}>
                        <div style={{ fontSize: 15, fontWeight: 500, color: textPrimary, marginBottom: 6 }}>
                            Aucune personne dans le graphe famille
                        </div>
                        <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                            Clique sur <strong>Personne</strong> dans la barre pour ajouter le premier membre.
                        </div>
                    </div>
                </div>
            )}

            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={NODE_TYPES}
                edgeTypes={EDGE_TYPES}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeClick={onNodeClick}
                onEdgeClick={onEdgeClick}
                onPaneClick={onPaneClick}
                fitView
                fitViewOptions={{ padding: 0.2 }}
                proOptions={{ hideAttribution: true }}
                minZoom={0.3}
                maxZoom={2}
                defaultEdgeOptions={{ type: 'relation' }}
            >
                <Background color={isDark ? '#2a2a44' : '#cbd5e1'} gap={18} size={1} />
                <Controls
                    position="bottom-right"
                    style={{
                        background: surfaceAlt,
                        border: `1px solid ${border}`,
                        borderRadius: 8,
                        overflow: 'hidden',
                    }}
                    showInteractive={false}
                />
                <MiniMap
                    pannable zoomable
                    position="top-right"
                    style={{
                        background: surfaceAlt,
                        border: `1px solid ${border}`,
                        borderRadius: 8,
                        marginTop: 52,
                    }}
                    nodeColor={(n) => n.data?.cohabitationColor || '#1D4ED8'}
                    nodeStrokeWidth={2}
                    maskColor={isDark ? 'rgba(0,0,0,0.4)' : 'rgba(15,23,42,0.06)'}
                />
            </ReactFlow>

            {/* Panneau détails personne */}
            {selectedPerson && (
                <PersonDetailPanel
                    person={selectedPerson}
                    relationships={relationships}
                    persons={persons}
                    cohabitations={cohabs}
                    projectId={projectId}
                    isDark={isDark}
                    onClose={() => setSelectedPersonId(null)}
                    onEdit={() => setEditingPerson({ open: true, person: selectedPerson })}
                    onAddRelation={() => setAddRelationSource(selectedPerson)}
                    onRemoveFromCohabitation={handleRemoveFromCohab}
                    onRefresh={() => loadGraph(true)}
                />
            )}

            {/* Modals */}
            <PersonEditModal
                projectId={projectId}
                person={editingPerson?.person}
                open={!!editingPerson?.open}
                onClose={() => setEditingPerson(null)}
                onSaved={() => loadGraph(true)}
                onDeleted={(id) => {
                    if (selectedPersonId === id) setSelectedPersonId(null);
                    loadGraph(true);
                }}
            />
            <CohabitationEditModal
                projectId={projectId}
                cohabitation={editingCohab?.cohabitation}
                persons={persons}
                open={!!editingCohab?.open}
                onClose={() => setEditingCohab(null)}
                onSaved={() => loadGraph(true)}
            />
            <CanvasSettingsModal
                projectId={projectId}
                open={showSettings}
                onClose={() => setShowSettings(false)}
                onSaved={() => loadGraph(true)}
            />
            <AddRelationModal
                projectId={projectId}
                source={addRelationSource}
                persons={persons}
                relationTypes={relationTypes}
                open={!!addRelationSource}
                onClose={() => setAddRelationSource(null)}
                onSaved={() => { setAddRelationSource(null); loadGraph(true); }}
                isDark={isDark}
            />
        </div>
    );
};

// ─── AddRelationModal inline (multi-cibles) ─────────────────────
const AddRelationModal = ({ projectId, source, persons, relationTypes, open, onClose, onSaved, isDark }) => {
    const [targetIds, setTargetIds] = useState(() => new Set());
    const [typeCode, setTypeCode] = useState('');
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (open) {
            setTargetIds(new Set());
            setTypeCode(relationTypes[0]?.code || '');
            setSearch('');
        }
    }, [open, relationTypes]);

    if (!open || !source) return null;

    const surface = isDark ? '#1a1a2e' : '#ffffff';
    const surfaceAlt = isDark ? '#16162a' : '#f8fafc';
    const textPrimary = isDark ? '#e5e7eb' : '#1e293b';
    const textSecondary = isDark ? '#94a3b8' : '#64748b';
    const border = isDark ? '#2a2a44' : '#e2e8f0';
    const inputStyle = {
        width: '100%', padding: '8px 10px',
        background: surfaceAlt, color: textPrimary,
        border: `1px solid ${border}`, borderRadius: 8,
        fontSize: 13, outline: 'none',
    };

    const toggleTarget = (id) => {
        setTargetIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const availableTargets = persons
        .filter((p) => p.id !== source.id)
        .filter((p) => !search.trim() || p.full_name.toLowerCase().includes(search.trim().toLowerCase()));

    const handleSave = async () => {
        if (targetIds.size === 0 || !typeCode) {
            toast.error('Sélectionne au moins une cible et un type');
            return;
        }
        setSaving(true);
        try {
            const results = await Promise.allSettled(
                [...targetIds].map((tid) => createRelationship(projectId, {
                    person_a_id: source.id,
                    person_b_id: Number(tid),
                    type: typeCode,
                }))
            );
            const ok = results.filter((r) => r.status === 'fulfilled').length;
            const ko = results.length - ok;
            if (ok > 0 && ko === 0) toast.success(`${ok} relation${ok > 1 ? 's' : ''} créée${ok > 1 ? 's' : ''}`);
            else if (ok > 0 && ko > 0) toast.warning(`${ok} réussie${ok > 1 ? 's' : ''}, ${ko} échec${ko > 1 ? 's' : ''}`);
            else toast.error(`${ko} échec${ko > 1 ? 's' : ''}`);
            if (ok > 0) onSaved?.();
        } finally {
            setSaving(false);
        }
    };

    const selectAll = () => setTargetIds(new Set(availableTargets.map((p) => p.id)));
    const clearAll = () => setTargetIds(new Set());

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 1100,
                background: isDark ? 'rgba(0,0,0,0.7)' : 'rgba(15,23,42,0.5)',
                backdropFilter: 'blur(6px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: surface, color: textPrimary, borderRadius: 14,
                    width: '100%', maxWidth: 500, maxHeight: '85vh',
                    boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
                    border: `1px solid ${border}`, overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{
                    padding: '18px 22px', borderBottom: `1px solid ${border}`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 600 }}>
                            <Link2 size={15} style={{ verticalAlign: -2, marginRight: 6 }} />
                            Nouvelle(s) relation(s)
                        </div>
                        <div style={{ fontSize: 12, color: textSecondary, marginTop: 2 }}>
                            Depuis <strong>{source.full_name}</strong> · coche une ou plusieurs personnes, un seul clic crée tout.
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{ background: 'transparent', border: 'none', color: textSecondary, cursor: 'pointer', padding: 4, borderRadius: 6 }}
                    >
                        <X size={18} />
                    </button>
                </div>
                <div style={{ padding: 22, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                        <div style={{ fontSize: 11, color: textSecondary, marginBottom: 5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                            Type de relation (appliqué à toutes les cibles)
                        </div>
                        <select value={typeCode} onChange={(e) => setTypeCode(e.target.value)} style={inputStyle}>
                            {relationTypes
                                .filter((t) => !['enfant', 'pupille', 'filleul'].includes(t.code))
                                .map((t) => (
                                    <option key={t.code} value={t.code}>{t.label}</option>
                                ))}
                        </select>
                        <div style={{ fontSize: 11, color: textSecondary, marginTop: 5, fontStyle: 'italic' }}>
                            {['pere', 'mere', 'grand_pere', 'grand_mere', 'tuteur_legal', 'parrain', 'marraine'].includes(typeCode)
                                ? `La réciproque (${typeCode === 'tuteur_legal' ? 'pupille' : typeCode === 'parrain' || typeCode === 'marraine' ? 'filleul' : 'enfant'}) est créée automatiquement en base.`
                                : relationTypes.find((t) => t.code === typeCode)?.is_symmetric
                                    ? 'Relation symétrique : un seul enregistrement par paire.'
                                    : 'Type asymétrique sans réciproque automatique.'}
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                        <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6,
                        }}>
                            <div style={{ fontSize: 11, color: textSecondary, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                Cibles ({targetIds.size} sélectionnée{targetIds.size > 1 ? 's' : ''})
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <button onClick={selectAll} style={miniBtn(textSecondary, border)}>Tout</button>
                                <button onClick={clearAll} style={miniBtn(textSecondary, border)}>Aucun</button>
                            </div>
                        </div>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Rechercher…"
                            style={{ ...inputStyle, marginBottom: 8 }}
                        />
                        <div style={{
                            flex: 1, minHeight: 120, maxHeight: 280, overflowY: 'auto',
                            border: `1px solid ${border}`, borderRadius: 8, background: surfaceAlt,
                        }}>
                            {availableTargets.length === 0 && (
                                <div style={{ padding: 16, fontSize: 12, color: textSecondary, fontStyle: 'italic', textAlign: 'center' }}>
                                    Aucune personne disponible
                                </div>
                            )}
                            {availableTargets.map((p) => {
                                const checked = targetIds.has(p.id);
                                return (
                                    <label
                                        key={p.id}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 10,
                                            padding: '8px 12px', cursor: 'pointer',
                                            borderBottom: `1px solid ${border}`,
                                            background: checked ? (isDark ? '#1D4ED820' : '#1D4ED810') : 'transparent',
                                            transition: 'background 0.12s',
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggleTarget(p.id)}
                                            style={{ width: 15, height: 15, cursor: 'pointer' }}
                                        />
                                        <span style={{ fontSize: 13, fontWeight: 500 }}>{p.full_name}</span>
                                        {p.role && (
                                            <span style={{ fontSize: 11, color: textSecondary, marginLeft: 'auto' }}>
                                                {p.role}
                                            </span>
                                        )}
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <div style={{
                    padding: '14px 22px', borderTop: `1px solid ${border}`,
                    display: 'flex', justifyContent: 'flex-end', gap: 8, background: surfaceAlt,
                }}>
                    <button
                        onClick={onClose}
                        disabled={saving}
                        style={{
                            background: 'transparent', color: textSecondary,
                            border: `1px solid ${border}`, borderRadius: 8,
                            padding: '8px 14px', fontSize: 13, cursor: 'pointer',
                        }}
                    >
                        Annuler
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving || targetIds.size === 0 || !typeCode}
                        style={{
                            background: '#1D4ED8', color: '#fff', border: 'none', borderRadius: 8,
                            padding: '8px 16px', fontSize: 13, fontWeight: 500,
                            cursor: (saving || targetIds.size === 0) ? 'not-allowed' : 'pointer',
                            opacity: (saving || targetIds.size === 0 || !typeCode) ? 0.6 : 1,
                            display: 'flex', alignItems: 'center', gap: 6,
                        }}
                    >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
                        Créer {targetIds.size > 0 ? `(${targetIds.size})` : ''}
                    </button>
                </div>
            </div>
        </div>
    );
};

const miniBtn = (color, border) => ({
    background: 'transparent',
    border: `1px solid ${border}`,
    color,
    borderRadius: 6,
    padding: '2px 8px',
    fontSize: 10,
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
});

// ─── ToolbarButton ─────────────────────────────────────────────
const ToolbarButton = ({ icon, label, onClick, primary, iconOnly, isDark, title }) => {
    const surface = isDark ? '#16162a' : '#ffffff';
    const border = isDark ? '#2a2a44' : '#e2e8f0';
    const textPrimary = isDark ? '#e5e7eb' : '#1e293b';

    return (
        <button
            onClick={onClick}
            title={title || label}
            style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: iconOnly ? '7px 9px' : '7px 12px',
                background: primary ? '#1D4ED8' : surface,
                color: primary ? '#fff' : textPrimary,
                border: primary ? 'none' : `1px solid ${border}`,
                borderRadius: 9, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'transform 0.12s, box-shadow 0.15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
            onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
        >
            {icon}
            {!iconOnly && label}
        </button>
    );
};

// ─── Export wrapper (Provider obligatoire pour xyflow) ────────
const FamilyGraph = (props) => (
    <ReactFlowProvider>
        <FamilyGraphInner {...props} />
    </ReactFlowProvider>
);

export default FamilyGraph;
